import axios from 'axios';
import {
  GenerateRequest,
  GenerateResponse,
  HealthResponse
} from '../types';

// ── API Base URL ──────────────────────────────────────────────

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://scaffoldbridge-production.up.railway.app';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── API Functions ─────────────────────────────────────────────

/// Check API health
export const checkHealth =
  async (): Promise<HealthResponse> => {
    const response = await apiClient.get<HealthResponse>(
      '/api/generate/health'
    );
    return response.data;
  };

/// Preview generated files without downloading
export const previewGeneration =
  async (request: GenerateRequest):
    Promise<GenerateResponse> => {
    const response = await apiClient.post<GenerateResponse>(
      '/api/generate/preview',
      request
    );
    return response.data;
  };

/// Generate and download ZIP file
export const generateAndDownload = async (
  request: GenerateRequest,
  token?: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const headers: any = {
    'Content-Type': 'application/json',
    'Accept': '*/*' // ← accept everything
  };

  if (token) {
    headers['Authorization'] =
      `Bearer ${token}`;
  }

  const response = await apiClient.post(
    '/api/generate',
    request,
    {
      responseType: 'blob',
      headers,
      validateStatus: (status) =>
        status < 500,
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round(
            (progressEvent.loaded * 100) /
            progressEvent.total);
          onProgress(progress);
        }
      }
    }
  );

  // Handle non-200 responses
  if (response.status !== 200) {
    const text = await
      new Response(response.data).text();

    // Parse JSON error ✅
    let json: any = null;
    try {
      json = JSON.parse(text);
    } catch {
      throw new Error(
        `Request failed: ${response.status}`);
    }

    // Specific error handling ✅
    if (response.status === 429) {
      const err = new Error(
        json?.message ||
        'Generation limit reached!');
      (err as any).status = 429;
      throw err;
    }

    if (response.status === 401) {
      throw new Error('Please login first!');
    }

    if (response.status === 403) {
      const err = new Error(
        json?.message || 'Pro plan required!');
      (err as any).status = 403;
      throw err;
    }

    throw new Error(
      json?.message || 'Generation failed');
  }

  // Extract filename ✅
  const contentDisposition =
    response.headers['content-disposition'];
  let fileName = 'scaffoldbridge_project.zip';

  if (contentDisposition) {
    const match = contentDisposition
      .match(/filename="?([^"]+)"?/);
    if (match) fileName = match[1];
  }

  // Download ZIP ✅
  const url = window.URL.createObjectURL(
    new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/// Get API base URL for display
export const getApiUrl = (): string => API_BASE_URL;

const API_URL = process.env.REACT_APP_API_URL
  || 'http://localhost:5062';

// ── Schema API ────────────────────────────────

export interface SavedSchema {
  id: number;
  name: string;
  description?: string;
  schemaJson: string;
  target: number;
  createdAt: string;
  updatedAt: string;
}

export interface SaveSchemaRequest {
  name: string;
  description?: string;
  schemaJson: string;
  target: number;
}

// Save new schema
export const saveSchema = async (
  request: SaveSchemaRequest,
  token: string
): Promise<SavedSchema> => {
  const response = await apiClient.post(
    '/api/schema',
    request,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  return response.data;
};

// Get all schemas
export const getSchemas = async (
  token: string
): Promise<SavedSchema[]> => {
  const response = await apiClient.get(
    '/api/schema',
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  return response.data;
};

// Get schema by id
export const getSchemaById = async (
  id: number,
  token: string
) => {
  const response = await apiClient.get(
    `/api/schema/${id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  return response.data;
};

// Update schema
export const updateSchema = async (
  id: number,
  request: SaveSchemaRequest,
  token: string
): Promise<SavedSchema> => {
  const response = await apiClient.put(
    `/api/schema/${id}`,
    request,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  return response.data;
};

// Delete schema
export const deleteSchema = async (
  id: number,
  token: string
): Promise<void> => {
  await apiClient.delete(
    `/api/schema/${id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
};